import { api } from './authService';

// 获取当前租户的API基础路径
function getApiBase(): string {
  const tenantCode = localStorage.getItem('currentTenant') || 'default';
  return `/${tenantCode}`;
}

// Template文件相关类型定义
export interface TemplateFile {
  id: number;
  tenant_code: string;
  template_name: string;
  template_code: string;
  display_name: string;
  description?: string;
  version: string;
  is_active: boolean;
  created_by?: string;
  is_synchronized?: boolean;
  last_sync_at?: string;
  company_id?: number; // 关联的公司ID
  business_type?: string; // 业务类型：CPO, SQ, SO, APInvoice等
  customer_id?: string; // 客户ID（字符串类型）
  created_at: string;
  updated_at: string;
}

export interface TemplateProperty {
  id: number;
  template_file_id: number;
  parent_property_id?: number;
  property_name: string;
  property_type: 'string' | 'number' | 'object' | 'array' | 'date' | 'boolean' | '[null]';
  display_name?: string;
  description?: string;
  is_required: boolean;
  sort_order: number;
  level: number;
  path: string;
  created_at: string;
  updated_at: string;
  children?: TemplateProperty[];
  array_items?: TemplateArrayItems;
}

export interface TemplateArrayItems {
  id: number;
  property_id: number;
  items_type: 'string' | 'number' | 'object' | 'date' | 'boolean';
  items_description?: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateVersion {
  id: number;
  template_file_id: number;
  version: string;
  change_description?: string;
  created_by?: string;
  created_at: string;
}

export interface TemplateFileWithProperties extends TemplateFile {
  properties: TemplateProperty[];
}

export interface CreateTemplateRequest {
  template_name: string;
  template_code: string;
  display_name: string;
  description?: string;
  template_content?: any; // JSON Template对象
  version?: string;
  is_active?: boolean;
  created_by?: string;
  company_id?: number; // 关联的公司ID
  business_type?: string; // 业务类型：CPO, SQ, SO, APInvoice等
  customer_id?: string; // 客户ID（字符串类型）
}

export interface UpdateTemplateRequest {
  template_name?: string;
  display_name?: string;
  description?: string;
  version?: string;
  is_active?: boolean;
  company_id?: number; // 关联的公司ID
  business_type?: string; // 业务类型：CPO, SQ, SO, APInvoice等
  customer_id?: string; // 客户ID（字符串类型）
}

export interface ImportTemplateRequest {
  json_content: string;
  template_name: string;
  template_code?: string;
  display_name?: string;
  description?: string;
  version?: string;
  is_active?: boolean;
  created_by?: string;
}

export interface CreateVersionRequest {
  version: string;
  change_description?: string;
  created_by?: string;
}

export const templateService = {
  // 获取所有Template文件
  async getTemplates(): Promise<TemplateFile[]> {
    const response = await api.get(`${getApiBase()}/templates`);
    return response.data.data || [];
  },

  // 获取Template文件及其完整属性结构
  async getTemplateWithProperties(templateCode: string): Promise<TemplateFileWithProperties> {
    const response = await api.get(`${getApiBase()}/templates/${templateCode}`);
    return response.data.data;
  },

  // 获取单个Template文件基本信息
  async getTemplate(templateCode: string): Promise<TemplateFile> {
    const response = await api.get(`${getApiBase()}/templates/${templateCode}`);
    return response.data.data;
  },

  // 根据ID获取Template文件基本信息
  async getTemplateById(id: number): Promise<TemplateFile> {
    const response = await api.get(`${getApiBase()}/templates/id/${id}`);
    return response.data.data;
  },

  // 创建Template文件
  async createTemplate(templateData: CreateTemplateRequest): Promise<TemplateFile> {
    const response = await api.post(`${getApiBase()}/templates`, templateData);
    return response.data.data;
  },

  // 更新Template文件
  async updateTemplate(id: number, updates: UpdateTemplateRequest): Promise<TemplateFile> {
    const response = await api.put(`${getApiBase()}/templates/${id}`, updates);
    return response.data.data;
  },

  // 删除Template文件
  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`${getApiBase()}/templates/${id}`);
  },

  // 转换为JSON Template格式
  async convertToJsonTemplate(id: number): Promise<any> {
    const response = await api.get(`${getApiBase()}/templates/${id}/json`);
    return response.data.data;
  },

  // 从JSON导入Template
  async importFromJson(importData: ImportTemplateRequest): Promise<TemplateFile> {
    const response = await api.post(`${getApiBase()}/templates/import`, importData);
    return response.data.data;
  },

  // 获取版本历史
  async getVersions(id: number): Promise<TemplateVersion[]> {
    const response = await api.get(`${getApiBase()}/templates/${id}/versions`);
    return response.data.data || [];
  },

  // 创建新版本
  async createVersion(id: number, versionData: CreateVersionRequest): Promise<TemplateVersion> {
    const response = await api.post(`${getApiBase()}/templates/${id}/versions`, versionData);
    return response.data.data;
  },

  // 导出Template为JSON字符串
  async exportTemplate(id: number): Promise<string> {
    const jsonTemplate = await this.convertToJsonTemplate(id);
    return JSON.stringify(jsonTemplate, null, 2);
  },

  // 验证Template名称是否可用
  async validateTemplateCode(templateCode: string): Promise<boolean> {
    try {
      await this.getTemplate(templateCode);
      return false; // 如果找到了，说明已存在
    } catch (error: any) {
      if (error.response?.status === 404) {
        return true; // 404表示不存在，可以使用
      }
      throw error; // 其他错误继续抛出
    }
  },

  // 同步文档文件到Template
  async syncDocumentFile(filename: string, createdBy?: string): Promise<TemplateFile> {
    const response = await api.post(`${getApiBase()}/templates/sync/${filename}`, {
      created_by: createdBy
    });
    return response.data.data;
  },

  // 批量同步文档文件
  async batchSyncDocumentFiles(filenames: string[], createdBy?: string): Promise<any> {
    const response = await api.post(`${getApiBase()}/templates/sync/batch`, {
      filenames,
      created_by: createdBy
    });
    return response.data.data;
  },

  // 更新同步状态
  async updateSyncStatus(id: number, isSynchronized: boolean): Promise<void> {
    await api.put(`${getApiBase()}/templates/${id}/sync-status`, {
      is_synchronized: isSynchronized
    });
  }
};
